import React, { useMemo, useCallback } from 'react';
import { Overlay, Popover, Button } from 'react-bootstrap';
import { FaPlay } from 'react-icons/fa';
import { VideoModal } from 'components/pages/VideoLibraryPage/VideoModal/VideoModal';
import { BranchStep, StepTypes } from '../../../../dorian-shared/types/branch/BranchStep';
import { usePopover } from './hooks/usePopover';
import { useVideoPlayer } from './hooks/useVideoPlayer';
import styles from './VideoStepsPopoverTrigger.module.scss';

type VideoStepsPopoverTriggerProps = {
  steps: BranchStep[];
  children: React.ReactNode;
};

export function VideoStepsPopoverTrigger({ steps, children }: VideoStepsPopoverTriggerProps) {
  const {
    showPopover,
    triggerRef,
    popoverRef,
    handleMouseEnter,
    handleMouseLeave,
  } = usePopover();

  const {
    videoToPlay,
    setVideoToPlay,
    handlePlayVideo,
  } = useVideoPlayer();

  const videoSteps = useMemo(() => steps
    .map((step, index) => ({ ...step, originalIndex: index }))
    .filter((step) => step.stepTypeId === StepTypes.Video && !!step.video), [steps]);

  const handleMouseLeaveWrapper = useCallback(() => {
    if (!videoToPlay) {
      handleMouseLeave();
    }
  }, [videoToPlay, handleMouseLeave]);

  const handleModalClose = useCallback(() => {
    setVideoToPlay(null);
    handleMouseLeave();
  }, [handleMouseLeave, setVideoToPlay]);

  return (
    <>
      <div
        ref={triggerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeaveWrapper}
      >
        {children}
      </div>

      <Overlay
        target={triggerRef.current}
        show={showPopover}
        placement="bottom"
      >
        <Popover
          id="popover-video-steps"
          onMouseOver={handleMouseEnter}
          onMouseLeave={handleMouseLeaveWrapper}
        >
          <Popover.Content
            ref={popoverRef}
            className={styles.popoverContent}
          >
            {videoSteps.length > 0 ? (
              <ul>
                {videoSteps.map((step) => (
                  <li key={step.id}>
                    <span
                      className={styles.stepInfo}
                      title={`${step.video?.title || 'Untitled Video'} (Step ${step.originalIndex})`}
                    >
                      Step
                      {' '}
                      {step.originalIndex}
                      :
                      {' '}
                      {step.video?.title || 'Untitled Video'}
                    </span>
                    <Button
                      variant="link"
                      onClick={() => handlePlayVideo(
                        step.video?.url || '',
                        step.video?.title || 'Untitled Video',
                        step.video?.videoSettings,
                      )}
                      title={`Play ${step.video?.title || 'Untitled Video'}`}
                      className={styles.playButton}
                    >
                      <FaPlay size={14} />
                    </Button>
                  </li>
                ))}
              </ul>
            ) : (
              'No video steps found.'
            )}
          </Popover.Content>
        </Popover>
      </Overlay>

      {videoToPlay && (
        <VideoModal
          video={{
            title: videoToPlay.title,
            videoUrl: videoToPlay.videoUrl,
          }}
          videoSettings={videoToPlay.videoSettings}
          isVisible={!!videoToPlay}
          onClose={handleModalClose}
        />
      )}
    </>
  );
}
